import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSupabaseClient } from 'src/api/supabase'
import { useLocalization } from 'src/shared/hooks/useLocalization'

export type UploadedLogo = {
  url: string
  filePath: string
}

type UseHandleLogosOptions = {
  org_id: string | undefined
  companyUrl?: string | null
  selectedLogoUrl?: string | null
  onLogoSelect?: (url: string | null) => void
  /** Skip listing illustration-logos bucket (e.g. when another component loads uploads) */
  skipStorageList?: boolean
}

export const useHandleLogos = ({
  org_id,
  companyUrl,
  selectedLogoUrl,
  onLogoSelect,
  skipStorageList = false,
}: UseHandleLogosOptions) => {
  const supabase = useSupabaseClient()
  const { t } = useLocalization()
  const [uploadedLogos, setUploadedLogos] = useState<UploadedLogo[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [brandfetchNonce, setBrandfetchNonce] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const refreshBrandfetch = useCallback(() => {
    setBrandfetchNonce((n) => n + 1)
  }, [])

  // Fetch all logos from the bucket
  useEffect(() => {
    if (!org_id) return

    const fetchLogos = async () => {
      const { data } = await supabase.storage
        .from('illustration-logos')
        .list(`${org_id}/`, {
          limit: 100,
          sortBy: { column: 'created_at', order: 'desc' },
        })

      if (data) {
        const logos = await Promise.all(
          data.map(async (file) => {
            const filePath = `${org_id}/${file.name}`
            const {
              data: { publicUrl },
            } = supabase.storage
              .from('illustration-logos')
              .getPublicUrl(filePath)
            return {
              url: publicUrl,
              filePath: filePath,
            } as UploadedLogo
          })
        )
        setUploadedLogos(logos)
      }
    }

    fetchLogos()
  }, [org_id, supabase, skipStorageList])

  // Generate image URLs from company URL (brandfetch); optional cache-bust on re-fetch
  const brandfetchUrls = useMemo(() => {
    if (!companyUrl) return []
    const baseUrls = [
      `https://cdn.brandfetch.io/${companyUrl}/fallback/404/w/400?c=1idPWY0AiNqr7y_koY9`,
      `https://cdn.brandfetch.io/${companyUrl}/fallback/404/w/400/logo?c=1idPWY0AiNqr7y_koY9`,
      `https://cdn.brandfetch.io/${companyUrl}/fallback/404/w/400/theme/light/logo?c=1idPWY0AiNqr7y_koY9`,
      `https://cdn.brandfetch.io/${companyUrl}/fallback/404/w/400/symbol?c=1idPWY0AiNqr7y_koY9`,
      `https://cdn.brandfetch.io/${companyUrl}/fallback/404/w/400/theme/light/symbol?c=1idPWY0AiNqr7y_koY9`,
    ]
    const suffix = brandfetchNonce > 0 ? `&_bf=${brandfetchNonce}` : ''
    return baseUrls.map((url) => `${url}${suffix}`)
  }, [companyUrl, brandfetchNonce])

  // Preload images when brandfetchUrls array is populated
  useEffect(() => {
    if (brandfetchUrls.length > 0) {
      brandfetchUrls.forEach((url) => {
        const img = new window.Image()
        img.src = url
      })
    }
  }, [brandfetchUrls])

  // Handle file upload
  const handleLogoUpload = async (e: any): Promise<string | null> => {
    const file = e.target.files?.[0]
    if (!file || !org_id) return null

    try {
      setIsUploading(true)
      const fileExt = file.name.split('.').pop()
      const fileName = `${Math.random()}.${fileExt}`
      const bucket = 'illustration-logos'
      const filePath = `${org_id}/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, file)

      if (uploadError) throw uploadError

      const {
        data: { publicUrl },
      } = supabase.storage.from(bucket).getPublicUrl(filePath)

      const newLogo: UploadedLogo = {
        url: publicUrl,
        filePath: filePath,
      }

      setUploadedLogos((prev) => [...prev, newLogo])

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      // Call selection callback if provided
      if (onLogoSelect) {
        onLogoSelect(publicUrl)
      }

      return publicUrl
    } catch (err) {
      console.error('Error uploading logo:', err)
      alert(
        t(
          'Failed to upload logo. Please try again.',
          'Kunde inte ladda upp logotypen. Försök igen.'
        )
      )
      return null
    } finally {
      setIsUploading(false)
    }
  }

  // Handle logo deletion
  const handleDeleteLogo = async (logo: UploadedLogo): Promise<boolean> => {
    if (
      !confirm(
        t(
          'Are you sure you want to delete this logo?',
          'Är du säker på att du vill ta bort denna logotyp?'
        )
      )
    )
      return false

    try {
      const bucket = 'illustration-logos'
      const { error: deleteError } = await supabase.storage
        .from(bucket)
        .remove([logo.filePath])

      if (deleteError) throw deleteError

      setUploadedLogos((prev) => prev.filter((l) => l.url !== logo.url))

      // If the deleted logo was selected, clear selection
      if (selectedLogoUrl === logo.url && onLogoSelect) {
        onLogoSelect(null)
      }

      return true
    } catch (err) {
      console.error('Error deleting logo:', err)
      alert(
        t(
          'Failed to delete logo. Please try again.',
          'Kunde inte ta bort logotypen. Försök igen.'
        )
      )
      return false
    }
  }

  return {
    uploadedLogos,
    brandfetchUrls,
    refreshBrandfetch,
    isUploading,
    fileInputRef,
    handleLogoUpload,
    handleDeleteLogo,
  }
}
